import React, { useEffect, useState } from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Card } from "@/components/ui/card";
import api, { API } from "@/lib/api";
import { getDLToken, ensureDLToken } from "@/lib/dlToken";
import { Phone, MapPin, Pill, User, Clock, FileSignature, Camera, MessageSquareText, AlertTriangle, Download, RefreshCw, CheckCircle2, QrCode } from "lucide-react";
import { fmtDateTime } from "@/lib/datetime";
import { formatPhone } from "@/lib/phone";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { formatApiError } from "@/lib/api";

const STATUS_COLORS = {
  pending: "bg-secondary text-secondary-foreground",
  assigned: "bg-primary/10 text-primary",
  in_transit: "bg-[#3a86ff]/15 text-[#1f4ea3]",
  delivered: "bg-accent/15 text-accent",
  failed: "bg-destructive/15 text-destructive",
};

function fmt(iso) { return fmtDateTime(iso); }

export default function OrderDetailDrawer({ orderId, open, onClose }) {
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(false);
  const [regeocoding, setRegeocoding] = useState(false);
  const { user } = useAuth();
  const canRegeocode = user?.role === "super_admin" || user?.role === "dispatcher";
  const [forceCompleting, setForceCompleting] = useState(false);

  // Super Admin escape hatch for the "driver said delivered but the POST
  // never landed" case (see /admin/orders/stuck). Bypasses the strict POD
  // validation (which only applies to `role === 'driver'`), stamps the
  // history with an explicit marker so the audit trail is honest.
  const forceComplete = async () => {
    if (!order) return;
    if (user?.role !== "super_admin") return;
    const label = order.order_number || order.id;
    if (!window.confirm(
      `Force-complete order ${label}?\n\nThis flips the status to Delivered on the server AND records a history entry noting that the original POD (photos + signature) was lost in the driver's offline queue.\n\nUse this ONLY when the driver has confirmed the delivery was actually made.`
    )) return;
    setForceCompleting(true);
    try {
      const fd = new FormData();
      fd.append("comment", "Force-completed by Super Admin — original POD lost in offline queue.");
      await api.post(`/orders/${order.id}/deliver`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(`Order ${label} marked delivered.`);
      // Re-fetch so the drawer reflects the new status + delivered_at.
      const { data } = await api.get(`/orders/${order.id}`);
      setOrder(data);
    } catch (e) {
      toast.error(`Force-complete failed: ${formatApiError(e)}`);
    } finally {
      setForceCompleting(false);
    }
  };

  useEffect(() => {
    if (!open || !orderId) return;
    setLoading(true);
    api.get(`/orders/${orderId}`).then((r) => setOrder(r.data)).finally(() => setLoading(false));
  }, [open, orderId]);

  const onRegeocode = async () => {
    if (!order) return;
    if (!confirm(`Re-geocode address?\n\n"${order.address}"\n\nThis re-resolves the pin from the address text and updates Miles. Use this when the pin is in the wrong place.`)) return;
    setRegeocoding(true);
    try {
      const { data } = await api.post(`/orders/${order.id}/regeocode`);
      setOrder(data);
      toast.success(`Address re-geocoded — pin moved, miles recomputed (${data.miles?.toFixed(1) ?? "—"} mi)`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setRegeocoding(false);
    }
  };

  // SEC-003: use a short-lived download token in `?auth=` query params so the
  // long access JWT never lands in browser history, server logs, or
  // referer headers.
  const dlAuth = encodeURIComponent(getDLToken());
  const fileUrl = (id) => `${API}/files/${id}?auth=${dlAuth}`;
  useEffect(() => {
    if (open) ensureDLToken().catch(() => {});
  }, [open]);

  return (
    <Sheet open={open} onOpenChange={(o) => !o && onClose?.()}>
      <SheetContent side="right" className="w-full sm:max-w-lg overflow-y-auto" data-testid="order-detail-drawer">
        <SheetHeader>
          <SheetTitle className="font-display text-2xl">Order details</SheetTitle>
        </SheetHeader>

        {loading && <div className="py-12 text-center text-muted-foreground text-sm">Loading…</div>}

        {!loading && order && (
          <div className="space-y-5 pt-4">
            <div className="flex items-center justify-between">
              <span className={`text-[11px] uppercase tracking-wide font-medium px-2.5 py-1 rounded-full ${STATUS_COLORS[order.status]}`}>
                {order.status.replace("_", " ")}
              </span>
              <span className="text-xs uppercase tracking-wide font-medium px-2.5 py-1 rounded-full bg-secondary text-secondary-foreground">
                {order.priority.replace("_", " ")}
              </span>
            </div>

            <Card className="p-4 rounded-xl border-border space-y-2.5">
              <div className="flex items-start gap-2.5">
                <User className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                <div className="text-sm">
                  <div className="font-semibold">{order.customer_name}</div>
                  <a href={`tel:${order.customer_phone}`} className="text-muted-foreground hover:underline flex items-center gap-1.5">
                    <Phone className="h-3 w-3" />{formatPhone(order.customer_phone)}
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <MapPin className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                <div className="text-sm flex-1">
                  <div className="flex items-start gap-2">
                    <span className="flex-1">{order.address}</span>
                    {canRegeocode && (
                      <button
                        type="button"
                        onClick={onRegeocode}
                        disabled={regeocoding}
                        className="shrink-0 inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-medium px-2 py-0.5 rounded-full border border-border bg-secondary/40 text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors disabled:opacity-50"
                        data-testid="drawer-regeocode-btn"
                        title="Re-geocode this address — use when the pin is in the wrong place"
                      >
                        <RefreshCw className={`h-3 w-3 ${regeocoding ? "animate-spin" : ""}`} />
                        {regeocoding ? "Re-geocoding…" : "Re-geocode"}
                      </button>
                    )}
                  </div>
                  {order.lat && <div className="text-xs text-muted-foreground font-mono-num mt-0.5">{order.lat.toFixed(4)}, {order.lng.toFixed(4)}</div>}
                </div>
              </div>
              <div className="flex items-start gap-2.5">
                <Pill className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                <div className="text-sm flex-1">
                  <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-1">From pharmacy</div>
                  <div>{order.pharmacy_name || "—"}</div>
                </div>
              </div>
              {order.primary_key && (
                <div className="flex items-start gap-2.5" data-testid="drawer-primary-key">
                  <QrCode className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                  <div className="text-sm flex-1">
                    <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-1">Primary Key (QR)</div>
                    <div className="font-mono-num tracking-wide" data-testid="drawer-primary-key-value">{order.primary_key}</div>
                  </div>
                </div>
              )}
              {order.driver_name && (
                <div className="flex items-start gap-2.5">
                  <User className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                  <div className="text-sm flex-1">
                    <div className="text-xs text-muted-foreground uppercase tracking-wide font-medium mb-1">Driver</div>
                    <div>{order.driver_name} {order.sequence && <span className="text-xs text-muted-foreground">· stop #{order.sequence}</span>}</div>
                  </div>
                </div>
              )}
            </Card>

            <Card className="p-4 rounded-xl border-border" data-testid="drawer-items">
              <div className="text-xs uppercase tracking-wide font-medium text-muted-foreground mb-3">Items ({order.items.length})</div>
              <ul className="space-y-2">
                {order.items.map((it, i) => (
                  <li key={i} className="flex items-start justify-between gap-3 text-sm border-b border-border last:border-b-0 pb-2 last:pb-0">
                    <div>
                      <div className="font-medium">{it.name}</div>
                      {it.notes && <div className="text-xs text-muted-foreground">{it.notes}</div>}
                    </div>
                    <span className="font-mono-num text-muted-foreground shrink-0">×{it.qty}</span>
                  </li>
                ))}
              </ul>
              {typeof order.copay === "number" && order.copay > 0 && (
                <div
                  className="mt-3 pt-3 border-t border-border flex items-center justify-between rounded-lg bg-[#fff4d6] border border-[#FF6D00]/45 px-3 py-2"
                  data-testid="drawer-copay"
                >
                  <span className="text-[11px] uppercase tracking-wider font-medium text-[#9c4500]">Copay to collect</span>
                  <span className="font-mono-num text-base font-semibold text-[#9c4500]">${order.copay.toFixed(2)}</span>
                </div>
              )}
            </Card>

            {order.notes && (
              <Card className="p-4 rounded-xl border-border bg-secondary/40">
                <div className="text-xs uppercase tracking-wide font-medium text-muted-foreground mb-1.5 flex items-center gap-1.5">
                  <MessageSquareText className="h-3.5 w-3.5" /> Delivery instructions
                </div>
                <div className="text-sm">{order.notes}</div>
              </Card>
            )}

            <Card className="p-4 rounded-xl border-border">
              <div className="text-xs uppercase tracking-wide font-medium text-muted-foreground mb-2 flex items-center gap-1.5">
                <Clock className="h-3.5 w-3.5" /> Order history
              </div>
              <div className="space-y-1.5 text-sm">
                {(!Array.isArray(order.history) || order.history.length === 0) && (
                  <>
                    <div className="flex items-center justify-between"><span className="text-muted-foreground">Created</span><span className="font-mono-num text-xs">{fmt(order.created_at)}</span></div>
                    {order.delivered_at && (
                      <div className="flex items-center justify-between"><span className="text-muted-foreground">{order.status === "delivered" ? "Delivered" : "Closed"}</span><span className="font-mono-num text-xs">{fmt(order.delivered_at)}</span></div>
                    )}
                  </>
                )}
                {Array.isArray(order.history) && order.history.length > 0 && (
                  <div className="space-y-2" data-testid="drawer-history">
                    {order.history.map((h, i) => {
                      const META = {
                        created:         { label: "Created",              cls: "text-[#234123]" },
                        pre_assigned:    { label: "Pre-assigned driver",  cls: "text-[#1f4ea3]" },
                        unassigned:      { label: "Driver cleared",       cls: "text-muted-foreground" },
                        assigned:        { label: "Assigned to route",    cls: "text-[#1f4ea3]" },
                        in_transit:      { label: "Out for delivery",     cls: "text-[#9c4500]" },
                        delivered:       { label: "Delivered",            cls: "text-[#234123]" },
                        pod_corrected:   { label: "POD corrected",        cls: "text-[#9c4500]" },
                        copay_collected: { label: "Copay collected",      cls: "text-[#234123]" },
                        copay_declined:  { label: "Copay not collected",  cls: "text-[#a73a1c]" },
                        failed:          { label: "Failed",               cls: "text-[#a73a1c]" },
                        returned:        { label: "Returned to pharmacy", cls: "text-[#7C3AED]" },
                      };
                      const m = META[h.type] || { label: (h.type || "Event").toUpperCase(), cls: "text-muted-foreground" };
                      return (
                        <div key={i} className="text-xs rounded-lg px-2.5 py-2 bg-secondary/50 border border-border/50" data-testid={`drawer-history-${h.type}-${i}`}>
                          <div className="flex items-center justify-between gap-2">
                            <span className={`font-medium uppercase tracking-wide text-[10px] ${m.cls}`}>{m.label}</span>
                            <span className="font-mono-num text-[10px] text-muted-foreground">{fmt(h.at)}</span>
                          </div>
                          <div className="mt-1 grid grid-cols-2 gap-x-3 gap-y-0.5 text-[11px]">
                            {h.by_name && <div><span className="text-muted-foreground">By:</span> <span className="font-medium">{h.by_name}</span></div>}
                            {h.return_date && <div><span className="text-muted-foreground">Return date:</span> <span className="font-mono-num">{h.return_date}</span></div>}
                            {h.driver_name && h.type === "assigned" && <div><span className="text-muted-foreground">Driver:</span> <span className="font-medium">{h.driver_name}</span></div>}
                            {h.sequence != null && <div><span className="text-muted-foreground">Stop #</span> <span className="font-mono-num">{h.sequence}</span></div>}
                            {typeof h.amount === "number" && <div><span className="text-muted-foreground">Amount:</span> <span className="font-mono-num font-semibold">${h.amount.toFixed(2)}</span></div>}
                            {h.method && <div><span className="text-muted-foreground">Method:</span> <span className="font-medium uppercase">{h.method}</span></div>}
                          </div>
                          {h.message && <div className="mt-1.5 text-foreground/90">{h.message}</div>}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </Card>

            {user?.role === "super_admin" && ["pending", "assigned", "in_transit"].includes(order.status) && (
              <Card
                className="p-4 rounded-xl border-2 border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-900/20"
                data-testid="drawer-force-complete-card"
              >
                <div className="flex items-start gap-3">
                  <AlertTriangle className="h-5 w-5 text-amber-700 dark:text-amber-300 mt-0.5 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-amber-900 dark:text-amber-100">
                      Driver reported delivered but the POD didn&apos;t land?
                    </div>
                    <div className="text-xs text-amber-800/90 dark:text-amber-200/80 mt-1">
                      Force-complete this order to flip its status to <b>Delivered</b> on the server. The history will note the POD was lost in the driver&apos;s offline queue.
                    </div>
                    <button
                      type="button"
                      onClick={forceComplete}
                      disabled={forceCompleting}
                      className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-600 hover:bg-amber-700 text-white text-xs font-medium disabled:opacity-50"
                      data-testid="drawer-force-complete-btn"
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" />
                      {forceCompleting ? "Completing…" : "Force complete"}
                    </button>
                  </div>
                </div>
              </Card>
            )}

            {order.status === "delivered" && (
              <Card className="p-4 rounded-xl border-border" data-testid="drawer-pod">
                <div className="flex items-center justify-between mb-3 gap-3">
                  <div className="text-xs uppercase tracking-wide font-medium text-muted-foreground">Proof of delivery</div>
                  <a
                    href={`${API}/orders/${order.id}/pod.pdf?auth=${dlAuth}`}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wide font-medium px-2.5 py-1 rounded-full bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
                    data-testid="drawer-pod-download"
                    title="Download POD as 1-page PDF"
                  >
                    <Download className="h-3 w-3" /> POD PDF
                  </a>
                </div>
                {order.pod_comment && (
                  <div className="text-sm mb-3 px-3 py-2 rounded-lg bg-secondary/50">{order.pod_comment}</div>
                )}
                <div className="grid grid-cols-3 gap-3">
                  {order.pod_photo_id ? (
                    <div>
                      <div className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1"><Camera className="h-3 w-3" /> Photo 1</div>
                      <a href={fileUrl(order.pod_photo_id)} target="_blank" rel="noreferrer">
                        <img src={fileUrl(order.pod_photo_id)} alt="POD photo 1" className="w-full rounded-lg border border-border object-cover h-40 hover:opacity-90 transition-opacity" data-testid="drawer-pod-photo" />
                      </a>
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground">No photo 1</div>
                  )}
                  {order.pod_photo_id_2 ? (
                    <div>
                      <div className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1"><Camera className="h-3 w-3" /> Photo 2</div>
                      <a href={fileUrl(order.pod_photo_id_2)} target="_blank" rel="noreferrer">
                        <img src={fileUrl(order.pod_photo_id_2)} alt="POD photo 2" className="w-full rounded-lg border border-border object-cover h-40 hover:opacity-90 transition-opacity" data-testid="drawer-pod-photo-2" />
                      </a>
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground">No photo 2</div>
                  )}
                  {(() => {
                    // Marquez per-Rx signature panel — mirrors the POD PDF
                    // layout. Only shown to Super Admin + Marquez staff on
                    // orders whose pharmacy is Marquez and that carry at
                    // least one `RX ` line-item + a captured signature.
                    // Everyone else (dispatcher, driver, other pharmacies)
                    // keeps seeing the standard single-signature cell.
                    const pharmName = (order.pharmacy_name || "").toLowerCase();
                    const isMarquez = pharmName.includes("marquez");
                    const rxItems = (order.items || []).filter((it) =>
                      /^rx\s/i.test(String(it?.name || "").trim())
                    );
                    const roleOk = user?.role === "super_admin" ||
                      (user?.role === "pharmacy_staff" && isMarquez);
                    const useRxLayout = roleOk && isMarquez && rxItems.length > 0 && !!order.pod_signature_id;
                    if (useRxLayout) {
                      return (
                        <div data-testid="drawer-pod-rx-signatures">
                          <div className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1">
                            <FileSignature className="h-3 w-3" /> Prescriptions received &amp; signed
                          </div>
                          <div className="rounded-lg border border-border bg-white divide-y divide-border h-40 overflow-y-auto">
                            {rxItems.map((it, i) => (
                              <div key={i} className="flex items-center gap-2 px-2 py-1.5" data-testid={`drawer-pod-rx-row-${i}`}>
                                <div className="w-24 shrink-0 font-mono-num text-[11px] text-foreground">
                                  {String(it.name).trim()}
                                </div>
                                <div className="relative flex-1 h-8">
                                  <img
                                    src={fileUrl(order.pod_signature_id)}
                                    alt={`Signature for ${it.name}`}
                                    className="absolute left-0.5 bottom-0.5 h-7 object-contain"
                                    style={{ maxWidth: "100%" }}
                                    data-testid={`drawer-pod-rx-signature-${i}`}
                                  />
                                  <div className="absolute left-0 right-0 bottom-0 border-b border-foreground/60" />
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    }
                    return order.pod_signature_id ? (
                      <div>
                        <div className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1"><FileSignature className="h-3 w-3" /> Signature</div>
                        <a href={fileUrl(order.pod_signature_id)} target="_blank" rel="noreferrer">
                          <img src={fileUrl(order.pod_signature_id)} alt="Signature" className="w-full rounded-lg border border-border bg-white object-contain h-40 hover:opacity-90 transition-opacity" data-testid="drawer-pod-signature" />
                        </a>
                      </div>
                    ) : (
                      <div className="text-xs text-muted-foreground">No signature</div>
                    );
                  })()}
                </div>
                {order.pod_photo_id_3 && (
                  <div className="mt-3 grid grid-cols-3 gap-3">
                    <div>
                      <div className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1">
                        <span className="inline-flex items-center px-1 py-0.5 rounded bg-destructive text-destructive-foreground text-[9px] font-bold uppercase">CII</span>
                        Recipient ID
                      </div>
                      <a href={fileUrl(order.pod_photo_id_3)} target="_blank" rel="noreferrer">
                        <img src={fileUrl(order.pod_photo_id_3)} alt="Recipient ID photo" className="w-full rounded-lg border border-destructive/30 object-cover h-40 hover:opacity-90 transition-opacity" data-testid="drawer-pod-photo-3" />
                      </a>
                    </div>
                  </div>
                )}
              </Card>
            )}

            {order.status === "failed" && (
              <Card className="p-4 rounded-xl border-destructive/40 bg-destructive/5" data-testid="drawer-pof">
                <div className="flex items-center justify-between mb-3 gap-3">
                  <div className="text-xs uppercase tracking-wide font-medium text-destructive flex items-center gap-1.5">
                    <AlertTriangle className="h-3.5 w-3.5" /> Proof of failure
                  </div>
                  {order.delivered_at && (
                    <div className="text-[11px] text-muted-foreground font-mono-num" data-testid="drawer-pof-at">
                      {fmt(order.delivered_at)}
                    </div>
                  )}
                </div>
                {order.failure_reason && (
                  <div className="mb-3">
                    <div className="text-xs uppercase tracking-wide font-medium text-muted-foreground mb-1 flex items-center gap-1">
                      <MessageSquareText className="h-3 w-3" /> Driver comment / reason
                    </div>
                    <div className="text-sm px-3 py-2 rounded-lg bg-destructive/10 text-foreground" data-testid="drawer-pof-reason">
                      {order.failure_reason}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-2 gap-3">
                  {order.fail_photo_id ? (
                    <div>
                      <div className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1"><Camera className="h-3 w-3" /> Failure photo</div>
                      <a href={fileUrl(order.fail_photo_id)} target="_blank" rel="noreferrer">
                        <img
                          src={fileUrl(order.fail_photo_id)}
                          alt="Failure photo"
                          className="w-full rounded-lg border border-destructive/30 object-cover h-40 hover:opacity-90 transition-opacity"
                          data-testid="drawer-pof-photo"
                        />
                      </a>
                    </div>
                  ) : (
                    <div className="text-xs text-muted-foreground italic">No photo captured</div>
                  )}
                  {order.failed_count > 1 && (
                    <div className="flex items-start">
                      <div className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full bg-destructive/15 text-destructive border border-destructive/40" data-testid="drawer-pof-attempts">
                        ↻ Attempt {order.failed_count}× · consider Return to Pharmacy
                      </div>
                    </div>
                  )}
                </div>
              </Card>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}
